import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { Helmet } from "react-helmet-async";

export default function ClientPortal() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("tredex_client_token");
  const [isLoggedIn, setLoggedIn] = useState(!!token);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const [insights, setInsights] = useState(null);
  const [knowledge, setKnowledge] = useState("");
  const [savingKb, setSavingKb] = useState(false);
  const [kbMsg, setKbMsg] = useState("");

  useEffect(() => {
    if (!isLoggedIn || !token) return;
    (async () => {
      try {
        const res = await fetch("/api/widget/client/me", {
          headers: { Authorization: "Bearer " + token },
        });
        if (!res.ok) throw new Error("Session expired");
        const d = await res.json();
        setMe(d);
        setKnowledge(d.knowledge || "");
        const ir = await fetch("/api/widget/client/insights", {
          headers: { Authorization: "Bearer " + token },
        });
        if (ir.ok) setInsights(await ir.json());
      } catch {
        sessionStorage.removeItem("tredex_client_token");
        setLoggedIn(false);
      }
    })();
  }, [isLoggedIn, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/widget/client/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId, password }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Login failed");
      sessionStorage.setItem("tredex_client_token", d.token);
      setLoggedIn(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("tredex_client_token");
    setLoggedIn(false);
    setMe(null);
    setInsights(null);
  };

  const handleSaveKnowledge = async () => {
    setSavingKb(true);
    setKbMsg("");
    try {
      const res = await fetch("/api/widget/client/knowledge", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ knowledge }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Save failed");
      setKbMsg("Knowledge base updated.");
    } catch (err) {
      setKbMsg("Error: " + err.message);
    } finally {
      setSavingKb(false);
    }
  };

  // ---- LOGIN VIEW ----
  if (!isLoggedIn) {
    return (
      <>
        <SEO title="Client Portal Login" noIndex />
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f6f7fb", fontFamily: "sans-serif" }}>
          <div style={{ background: "#fff", padding: "40px 36px", borderRadius: "16px", boxShadow: "0 8px 30px rgba(0,0,0,.08)", width: "100%", maxWidth: "400px" }}>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <i className="fas fa-comment-dots" style={{ fontSize: "2.5rem", color: "#6C63FF" }}></i>
              <h1 style={{ fontFamily: "Space Grotesk, var(--font-display)", marginTop: "8px" }}>Client Portal</h1>
              <p style={{ color: "#888", fontSize: "14px" }}>Sign in as <strong>{clientId}</strong></p>
            </div>
            {error && <div style={{ color: "#FF5F56", background: "#fff0f0", padding: "10px", borderRadius: "8px", marginBottom: "16px", fontSize: "14px", textAlign: "center" }}>{error}</div>}
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "6px", color: "#555" }}>Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your portal password" required
                  style={{ width: "100%", padding: "12px 14px", border: "1px solid #e0e2eb", borderRadius: "8px", fontSize: "15px", outline: "none", boxSizing: "border-box" }} />
              </div>
              <button type="submit" disabled={busy} style={{ width: "100%", padding: "12px", background: "#6C63FF", color: "#fff", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: 600, cursor: "pointer" }}>
                {busy ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  // ---- DASHBOARD VIEW ----
  const feat = (k) => (me && me.features && me.features[k]) || false;
  return (
    <>
      <SEO title={me ? me.businessName + " — Portal" : "Client Portal"} noIndex />
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "30px 20px", fontFamily: "sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <div>
            <h1 style={{ margin: 0 }}><i className="fas fa-comment-dots" style={{ color: "#6C63FF" }}></i> {me ? me.businessName : "Portal"}</h1>
            <p style={{ color: "#888", margin: "4px 0 0", fontSize: "14px" }}>Your AI Chat Widget Dashboard</p>
          </div>
          <button onClick={handleLogout} style={{ padding: "8px 16px", background: "transparent", border: "1px solid #ddd", borderRadius: "8px", cursor: "pointer", fontSize: "14px" }}>Logout</button>
        </div>

        {me && <div style={{ background: "#fff", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,.06)", marginBottom: "20px" }}>
          <h3 style={{ margin: "0 0 12px" }}><i className="fas fa-star"></i> Your Plan &amp; Features</h3>
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ padding: "6px 14px", background: "#e4f2e4", borderRadius: "20px", fontWeight: 600, fontSize: "13px", color: "#2a7a2a" }}>✅ Chat</span>
            <span style={{ padding: "6px 14px", background: feat("voice") ? "#e4f2e4" : "#f0f0f0", borderRadius: "20px", fontWeight: 600, fontSize: "13px", color: feat("voice") ? "#2a7a2a" : "#999" }}>{feat("voice") ? "🎙 Voice" : "❌ Voice"}</span>
            <span style={{ padding: "6px 14px", background: feat("report") ? "#e4f2e4" : "#f0f0f0", borderRadius: "20px", fontWeight: 600, fontSize: "13px", color: feat("report") ? "#2a7a2a" : "#999" }}>{feat("report") ? "📊 Reports" : "❌ Reports"}</span>
          </div>
          <p style={{ marginTop: "10px", fontSize: "12px", color: "#999" }}>Chat is always on (your embed widget). Voice adds mic input. Reports email insights.</p>
          <p style={{ marginTop: "14px", fontSize: "13px", color: "#888" }}>Embed: <code>{me.embedCode}</code></p>
        </div>}

        <div style={{ background: "#fff", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,.06)", marginBottom: "20px" }}>
          <h3 style={{ margin: "0 0 12px" }}><i className="fas fa-file-alt"></i> Knowledge Base</h3>
          {kbMsg && <p style={{ fontSize: "14px", marginBottom: "10px" }}>{kbMsg}</p>}
          <textarea rows={8} value={knowledge} onChange={(e) => setKnowledge(e.target.value)} placeholder="Your business details"
            style={{ width: "100%", padding: "12px", border: "1px solid #e0e2eb", borderRadius: "8px", fontSize: "14px", outline: "none", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }} />
          <button onClick={handleSaveKnowledge} disabled={savingKb} style={{ marginTop: "12px", padding: "10px 24px", background: "#6C63FF", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
            {savingKb ? "Saving..." : "Save Knowledge Base"}
          </button>
          <p style={{ marginTop: "8px", fontSize: "13px", color: "#888" }}>Changes apply immediately.</p>
        </div>

        {insights && <div style={{ background: "#fff", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,.06)" }}>
          <h3 style={{ margin: "0 0 8px" }}><i className="fas fa-chart-line"></i> Top Questions ({insights.total})</h3>
          {insights.topQuestions?.length > 0 ? (
            <ol style={{ paddingLeft: "20px" }}>
              {insights.topQuestions.slice(0,10).map((q,i) => <li key={i} style={{ marginBottom: "6px", fontSize: "14px" }}>{q.message} <span style={{ background: "#6C63FF", color: "#fff", borderRadius: "12px", padding: "1px 8px", fontSize: "11px" }}>{q.count}x</span></li>)}
            </ol>
          ) : <p style={{ color: "#888", fontSize: "14px" }}>No questions yet.</p>}
          {insights.regions?.length > 0 && <>
            <h4 style={{ margin: "16px 0 8px", fontSize: "14px", color: "#555" }}>Regions</h4>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>{insights.regions.map((r,i) => <span key={i} style={{ padding: "4px 12px", background: "#f0f4ff", borderRadius: "16px", fontSize: "13px" }}>{r.name} ({r.count})</span>)}</div>
          </>}
        </div>}
      </div>
    </>
  );
}